/**
 * User Table Component
 * Table displaying user list with actions (edit invites, change role, block)
 */
import React, { useState } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  Badge,
  Text,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { MoreVertical, Edit, Shield, Ban, CheckCircle } from 'lucide-react';
import { type AdminUser } from '../../services/AdminService';
import { format } from 'date-fns';
import EditInvitesModal from './EditInvitesModal';
import ChangeRoleModal from './ChangeRoleModal';
import BlockUserModal from './BlockUserModal';

interface UserTableProps {
  users: AdminUser[];
  onRefresh: () => void;
}

export const UserTable: React.FC<UserTableProps> = ({ users, onRefresh }) => {
  // Modal states
  const [editInvitesUser, setEditInvitesUser] = useState<AdminUser | null>(null);
  const [changeRoleUser, setChangeRoleUser] = useState<AdminUser | null>(null);
  const [blockUser, setBlockUser] = useState<AdminUser | null>(null);

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'N/A';
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role: string) => {
    return role === 'admin' ? 'purple' : 'blue';
  };

  // Get status badge
  const getStatusBadge = (user: AdminUser) => {
    if (user.is_blocked) {
      return <Badge colorScheme="red">Blocked</Badge>;
    }
    return <Badge colorScheme="green">Active</Badge>;
  };

  return (
    <>
      <Table variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th>User</Th>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th>Status</Th>
            <Th isNumeric>Invites</Th>
            <Th>Invited By</Th>
            <Th>Created At</Th>
            <Th>Last Login</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {users.map((user) => (
            <Tr key={user.id} _hover={{ bg: 'gray.50' }}>
              {/* User */}
              <Td>
                <HStack spacing={3}>
                  <Avatar size="sm" name={user.username} src={user.avatar || undefined} />
                  <Text fontWeight="medium">{user.username}</Text>
                </HStack>
              </Td>

              {/* Email */}
              <Td>
                <Text fontSize="sm" color="gray.600">
                  {user.email}
                </Text>
              </Td>

              {/* Role */}
              <Td>
                <Badge colorScheme={getRoleBadgeColor(user.role)}>
                  {user.role.toUpperCase()}
                </Badge>
              </Td>

              {/* Status */}
              <Td>{getStatusBadge(user)}</Td>

              {/* Invites */}
              <Td isNumeric>
                <Text fontWeight="medium">
                  {user.role === 'admin' ? '∞' : user.invites_available}
                </Text>
              </Td>

              {/* Invited By */}
              <Td>
                {user.invited_by ? (
                  <Text fontSize="sm" color="gray.600">
                    {user.invited_by.username}
                  </Text>
                ) : (
                  <Text fontSize="sm" color="gray.400">
                    —
                  </Text>
                )}
              </Td>

              {/* Created At */}
              <Td>
                <Text fontSize="sm" color="gray.600">
                  {formatDate(user.created_at)}
                </Text>
              </Td>

              {/* Last Login */}
              <Td>
                <Text fontSize="sm" color="gray.600">
                  {user.last_login ? formatDate(user.last_login) : 'Never'}
                </Text>
              </Td>

              {/* Actions */}
              <Td>
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<MoreVertical size={16} />}
                    variant="ghost"
                    size="sm"
                  />
                  <MenuList>
                    <MenuItem
                      icon={<Edit size={16} />}
                      onClick={() => setEditInvitesUser(user)}
                    >
                      Edit Invites
                    </MenuItem>
                    <MenuItem
                      icon={<Shield size={16} />}
                      onClick={() => setChangeRoleUser(user)}
                    >
                      Change Role
                    </MenuItem>
                    <MenuItem
                      icon={user.is_blocked ? <CheckCircle size={16} /> : <Ban size={16} />}
                      onClick={() => setBlockUser(user)}
                      color={user.is_blocked ? 'green.500' : 'red.500'}
                    >
                      {user.is_blocked ? 'Unblock User' : 'Block User'}
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Modals */}
      {editInvitesUser && (
        <EditInvitesModal
          user={editInvitesUser}
          isOpen={!!editInvitesUser}
          onClose={() => setEditInvitesUser(null)}
          onSuccess={onRefresh}
        />
      )}

      {changeRoleUser && (
        <ChangeRoleModal
          user={changeRoleUser}
          isOpen={!!changeRoleUser}
          onClose={() => setChangeRoleUser(null)}
          onSuccess={onRefresh}
        />
      )}

      {blockUser && (
        <BlockUserModal
          user={blockUser}
          isOpen={!!blockUser}
          onClose={() => setBlockUser(null)}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
};

export default UserTable;
